<template>
  <div style="margin:20px">
    <FiPageRail title="API Usage Dashboard" />

    <div style="position: relative; font-size: 20px; font-weight: bold; gap: 6px;" class="align-right d-flex align-items-center justify-content-center flex-wrap">
      <a href="#" class="arrows" v-on:click.prevent="shift_week(-1)">&lt;</a>
      &nbsp;&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="start_date" :config="date_options" :placeholder="start_date"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="end_date" :config="date_options" :placeholder="end_date"/>
      &nbsp;<a href="#" class="arrows" v-on:click.prevent="shift_week(1)">&gt;</a>
      &nbsp;&nbsp;|&nbsp;&nbsp;Tenant&nbsp;<b-form-select v-model="selected_tenant" :options="computed_tenant_options" style="width: 220px;" @change="selected_tenant_type = ''; load_data()"></b-form-select>
      &nbsp;&nbsp;Type&nbsp;<b-form-select v-model="selected_tenant_type" :options="tenant_type_options" style="width: 110px;" @change="load_data()"></b-form-select>
      &nbsp;&nbsp;|&nbsp;&nbsp;View&nbsp;<b-form-select v-model="global_view_mode" :options="global_view_mode_options" style="width: 180px;"></b-form-select>
      &nbsp;&nbsp;|&nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
    </div>
    <br/>

    <div v-if="loading" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <!-- ===================== SUMMARY: ALL CATEGORIES ===================== -->
    <div v-if="!loading && summary_chart_data" style="margin-top: 20px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #37474f; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_summary_section = !show_summary_section">
        <span>Summary — Requests Across All Categories</span>
        <span style="font-size: 14px;">{{ show_summary_section ? '▲' : '▼' }}</span>
      </div>
      <div v-if="show_summary_section">
        <dashboard-chart
            :loading="loading"
            :chart_data="summary_chart_data"
            chart_type="LineChart"
            graph_title="Total Requests By Date — All Categories"
            date_format="MM/dd"
            graph_height="300"
            :chart_colors="summary_chart_colors"
        />
      </div>
    </div>

    <!-- ===================== TOP 10 TENANTS ===================== -->
    <div v-if="!loading && top_tenants_data.length > 0 && !selected_tenant" style="margin-top: 30px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #37474f; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_top_tenants_section = !show_top_tenants_section">
        <span>Top {{ top_tenants_data.length }} Tenants by Total Requests</span>
        <span style="font-size: 14px;">{{ show_top_tenants_section ? '▲' : '▼' }}</span>
      </div>
      <div v-if="show_top_tenants_section" style="padding-top: 15px;">
        <dashboard-chart
            v-if="top_tenants_chart_data"
            :loading="loading"
            :chart_data="top_tenants_chart_data"
            chart_type="BarChart"
            graph_title="Top Tenants — Requests by Category"
            date_format=""
            graph_height="380"
            :chart_area="top_tenants_chart_area"
            :chart_colors="top_tenants_chart_colors"
        />
        <b-table
            :items="top_tenants_data"
            :fields="top_tenants_fields"
            striped hover small bordered
            sort-by="total"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(internal)="data">{{ (data.value || 0).toLocaleString() }}</template>
          <template #cell(tbn)="data">{{ (data.value || 0).toLocaleString() }}</template>
          <template #cell(telematics)="data">{{ (data.value || 0).toLocaleString() }}</template>
          <template #cell(gmaps)="data">{{ (data.value || 0).toLocaleString() }}</template>
          <template #cell(total)="data">{{ (data.value || 0).toLocaleString() }}</template>
        </b-table>
      </div>
    </div>

    <!-- ===================== TOP 10 API ENDPOINTS ===================== -->
    <div v-if="!loading && top_apis_data.length > 0" style="margin-top: 30px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #37474f; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_top_apis_section = !show_top_apis_section">
        <span>Top {{ top_apis_data.length }} API Endpoints by Total Requests</span>
        <span style="font-size: 14px;">{{ show_top_apis_section ? '▲' : '▼' }}</span>
      </div>
      <div v-if="show_top_apis_section" style="padding-top: 15px;">
        <dashboard-chart
            v-if="top_apis_chart_data"
            :loading="loading"
            :chart_data="top_apis_chart_data"
            chart_type="BarChart"
            graph_title="Top API Endpoints — Requests by Category"
            date_format=""
            graph_height="380"
            :chart_area="top_apis_chart_area"
            :chart_colors="top_apis_chart_colors"
        />
        <b-table
            :items="top_apis_data"
            :fields="top_apis_fields"
            striped hover small bordered
            sort-by="total"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(method)="data">{{ data.value || '—' }}</template>
          <template #cell(total)="data">{{ (data.value || 0).toLocaleString() }}</template>
        </b-table>
      </div>
    </div>

    <!-- ===================== INTERNAL API USAGE ===================== -->
    <div v-if="!loading && usage_data">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #2c3e50; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between; margin-top: 30px;"
           v-on:click="show_internal_section = !show_internal_section">
        <span>Internal API Usage</span>
        <span style="font-size: 14px;">{{ show_internal_section ? '▲' : '▼' }}</span>
      </div>

      <div v-if="show_internal_section" style="padding-top: 15px;">
        <div style="margin-bottom: 10px;">
          <span style="font-size: 20px; font-weight: bold;">Total Requests: {{ usage_data.total_requests.toLocaleString() }}</span>
        </div>

        <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
          <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">Filter Chart:</span>
          <b-form-select v-model="selected_chart_path" :options="computed_chart_path_options" style="width: 400px;" @change="update_chart"></b-form-select>
        </div>

        <dashboard-chart
            v-if="requests_by_date_chart"
            :loading="loading"
            :chart_data="requests_by_date_chart"
            chart_type="LineChart"
            :graph_title="chart_title"
            date_format="MM/dd"
            graph_height="300"
        />

        <div v-if="!selected_tenant && usage_data.by_tenant && usage_data.by_tenant.length > 0" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="show_tenant_table = !show_tenant_table">
            <span>Usage By Tenant ({{ internal_tenant_table_items.length }})</span>
            <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(internal_tenant_table_items, tenant_fields, 'internal_usage_by_tenant')">Download CSV</b-button>
          </div>
          <b-table
              v-if="show_tenant_table"
              :items="_limit(internal_tenant_table_items, 'int_tenant')"
              :fields="tenant_fields"
              striped hover small bordered
              sort-by="count"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="show_tenant_table && internal_tenant_table_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'int_tenant', !table_expanded['int_tenant'])" style="color: var(--fi-rail-accent); text-decoration: none;">
              {{ table_expanded['int_tenant'] ? '▲ Show less' : '▼ Show ' + (internal_tenant_table_items.length - 10) + ' more' }}
            </a>
          </div>
        </div>

        <div style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="show_path_table = !show_path_table">
            <span>Usage By API Endpoint ({{ filtered_internal_by_path.length }})</span>
            <b-button
                v-if="filtered_internal_by_path.length > 0"
                size="sm"
                variant="light"
                v-on:click.stop="downloadUsageCsv(filtered_internal_by_path, path_fields, 'internal_usage_by_endpoint')"
            >Download CSV</b-button>
          </div>
          <div v-if="show_path_table" class="mt-2" style="max-width: 480px;">
            <b-form-input
                v-model="internal_endpoint_filter"
                placeholder="Filter by path or HTTP method..."
                size="sm"
                @input="$set(table_expanded, 'int_path', false)"
            />
          </div>
          <b-table
              v-if="show_path_table"
              :items="_limit(filtered_internal_by_path, 'int_path')"
              :fields="path_fields"
              striped hover small bordered
              sort-by="count"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(method)="data">
              {{ data.value || '—' }}
            </template>
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="show_path_table && filtered_internal_by_path.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'int_path', !table_expanded['int_path'])" style="color: var(--fi-rail-accent); text-decoration: none;">
              {{ table_expanded['int_path'] ? '▲ Show less' : '▼ Show ' + (filtered_internal_by_path.length - 10) + ' more' }}
            </a>
          </div>
        </div>

        <div v-if="usage_data.by_date && usage_data.by_date.length > 0" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="show_date_table = !show_date_table">
            <span>Usage By Date ({{ usage_data.by_date.length }})</span>
            <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(usage_data.by_date, date_fields, 'internal_usage_by_date')">Download CSV</b-button>
          </div>
          <b-table
              v-if="show_date_table"
              :items="_limit(usage_data.by_date, 'int_date')"
              :fields="date_fields"
              striped hover small bordered
              sort-by="date"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="show_date_table && usage_data.by_date.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'int_date', !table_expanded['int_date'])" style="color: var(--fi-rail-accent); text-decoration: none;">
              {{ table_expanded['int_date'] ? '▲ Show less' : '▼ Show ' + (usage_data.by_date.length - 10) + ' more' }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== TBN API USAGE ===================== -->
    <div v-if="!loading && tbn_usage_data && selected_tenant_type !== 'CM'" style="margin-top: 50px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #d35400; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_tbn_section = !show_tbn_section">
        <span>TBN API Usage</span>
        <span style="font-size: 14px;">{{ show_tbn_section ? '▲' : '▼' }}</span>
      </div>

      <div v-if="show_tbn_section" style="padding-top: 15px;">
      <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
        <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">View:</span>
        <b-form-select v-model="tbn_view_mode" :options="tbn_view_mode_options" style="width: 250px;" @change="on_tbn_view_change"></b-form-select>
      </div>

      <div style="margin-bottom: 10px;">
        <span style="font-size: 20px; font-weight: bold;">{{ tbn_view_label }}: {{ tbn_view_total.toLocaleString() }}</span>
        <span v-if="tbn_usage_data.avg_response_bytes" style="font-size: 14px; color: #666; margin-left: 12px;">
          Avg Response Size: {{ formatBytes(tbn_usage_data.avg_response_bytes) }}
        </span>
      </div>

      <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
        <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">Filter Chart:</span>
        <b-form-select v-model="tbn_selected_chart_path" :options="tbn_computed_chart_path_options" style="width: 400px;" @change="update_tbn_chart"></b-form-select>
      </div>

      <dashboard-chart
          v-if="tbn_requests_by_date_chart"
          :loading="loading"
          :chart_data="tbn_requests_by_date_chart"
          chart_type="LineChart"
          :graph_title="tbn_chart_title"
          date_format="MM/dd"
          graph_height="300"
      />

      <div v-if="!selected_tenant && tbn_tenant_table_items.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: white; background-color: #e67e22; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
             v-on:click="tbn_show_tenant_table = !tbn_show_tenant_table">
          <span>Usage By Tenant ({{ tbn_tenant_table_items.length }})</span>
          <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(tbn_tenant_table_items, tbn_tenant_fields, 'tbn_usage_by_tenant')">Download CSV</b-button>
        </div>
        <b-table
            v-if="tbn_show_tenant_table"
            :items="_limit(tbn_tenant_table_items, 'tbn_tenant')"
            :fields="tbn_tenant_fields"
            striped hover small bordered
            sort-by="count"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="tbn_show_tenant_table && tbn_tenant_table_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'tbn_tenant', !table_expanded['tbn_tenant'])" style="color: #e67e22; text-decoration: none;">
            {{ table_expanded['tbn_tenant'] ? '▲ Show less' : '▼ Show ' + (tbn_tenant_table_items.length - 10) + ' more' }}
          </a>
        </div>
      </div>

      <div v-if="tbn_view_paths.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: white; background-color: #e67e22; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
             v-on:click="tbn_show_path_table = !tbn_show_path_table">
          <span>Usage By TBN Endpoint ({{ filtered_tbn_view_paths.length }})</span>
          <b-button
              v-if="filtered_tbn_view_paths.length > 0"
              size="sm"
              variant="light"
              v-on:click.stop="downloadUsageCsv(filtered_tbn_view_paths, tbn_path_fields, 'tbn_usage_by_endpoint')"
          >Download CSV</b-button>
        </div>
        <div v-if="tbn_show_path_table" class="mt-2" style="max-width: 480px;">
          <b-form-input
              v-model="tbn_endpoint_filter"
              placeholder="Filter by path or HTTP method..."
              size="sm"
              @input="$set(table_expanded, 'tbn_path', false)"
          />
        </div>
        <b-table
            v-if="tbn_show_path_table"
            :items="_limit(filtered_tbn_view_paths, 'tbn_path')"
            :fields="tbn_path_fields"
            striped hover small bordered
            sort-by="count"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(method)="data">
            {{ data.value || '—' }}
          </template>
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="tbn_show_path_table && filtered_tbn_view_paths.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'tbn_path', !table_expanded['tbn_path'])" style="color: #e67e22; text-decoration: none;">
            {{ table_expanded['tbn_path'] ? '▲ Show less' : '▼ Show ' + (filtered_tbn_view_paths.length - 10) + ' more' }}
          </a>
        </div>
      </div>

      <div v-if="tbn_view_dates.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: white; background-color: #e67e22; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
             v-on:click="tbn_show_date_table = !tbn_show_date_table">
          <span>Usage By Date ({{ tbn_view_dates.length }})</span>
          <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(tbn_view_dates, tbn_date_fields, 'tbn_usage_by_date')">Download CSV</b-button>
        </div>
        <b-table
            v-if="tbn_show_date_table"
            :items="_limit(tbn_view_dates, 'tbn_date')"
            :fields="tbn_date_fields"
            striped hover small bordered
            sort-by="date"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="tbn_show_date_table && tbn_view_dates.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'tbn_date', !table_expanded['tbn_date'])" style="color: #e67e22; text-decoration: none;">
            {{ table_expanded['tbn_date'] ? '▲ Show less' : '▼ Show ' + (tbn_view_dates.length - 10) + ' more' }}
          </a>
        </div>
      </div>
      </div>
    </div>

    <!-- ===================== TELEMATICS API USAGE ===================== -->
    <div v-if="!loading && telematics_usage_data" style="margin-top: 50px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #27ae60; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_telematics_section = !show_telematics_section">
        <span>Telematics API Usage</span>
        <span style="font-size: 14px;">{{ show_telematics_section ? '▲' : '▼' }}</span>
      </div>

      <div v-if="show_telematics_section" style="padding-top: 15px;">
        <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
          <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">View:</span>
          <b-form-select v-model="telematics_view_mode" :options="telematics_view_mode_options" style="width: 250px;" @change="on_telematics_view_change"></b-form-select>
        </div>

        <div style="margin-bottom: 10px;">
          <span style="font-size: 20px; font-weight: bold;">{{ telematics_view_label }}: {{ telematics_view_total.toLocaleString() }}</span>
        </div>

        <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
          <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">Filter Chart:</span>
          <b-form-select v-model="telematics_selected_chart_path" :options="telematics_computed_chart_path_options" style="width: 400px;" @change="update_telematics_chart"></b-form-select>
        </div>

        <dashboard-chart
            v-if="telematics_requests_by_date_chart"
            :loading="loading"
            :chart_data="telematics_requests_by_date_chart"
            chart_type="LineChart"
            :graph_title="telematics_chart_title"
            date_format="MM/dd"
            graph_height="300"
        />

        <div v-if="!selected_tenant && telematics_usage_data.by_provider && telematics_usage_data.by_provider.length > 0 && telematics_view_mode === 'all'" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: #2ecc71; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="telematics_show_provider_table = !telematics_show_provider_table">
            <span>Usage By Provider ({{ telematics_usage_data.by_provider.length }})</span>
            <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(telematics_usage_data.by_provider, telematics_provider_fields, 'telematics_usage_by_provider')">Download CSV</b-button>
          </div>
          <b-table
              v-if="telematics_show_provider_table"
              :items="_limit(telematics_usage_data.by_provider, 'tel_provider')"
              :fields="telematics_provider_fields"
              striped hover small bordered
              sort-by="count"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="telematics_show_provider_table && telematics_usage_data.by_provider.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'tel_provider', !table_expanded['tel_provider'])" style="color: #27ae60; text-decoration: none;">
              {{ table_expanded['tel_provider'] ? '▲ Show less' : '▼ Show ' + (telematics_usage_data.by_provider.length - 10) + ' more' }}
            </a>
          </div>
        </div>

        <div v-if="!selected_tenant && telematics_usage_data.by_tenant && telematics_usage_data.by_tenant.length > 0 && telematics_view_mode === 'all'" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: #2ecc71; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="telematics_show_tenant_table = !telematics_show_tenant_table">
            <span>Usage By Tenant ({{ telematics_tenant_table_items.length }})</span>
            <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(telematics_tenant_table_items, tenant_fields, 'telematics_usage_by_tenant')">Download CSV</b-button>
          </div>
          <b-table
              v-if="telematics_show_tenant_table"
              :items="_limit(telematics_tenant_table_items, 'tel_tenant')"
              :fields="tenant_fields"
              striped hover small bordered
              sort-by="count"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="telematics_show_tenant_table && telematics_tenant_table_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'tel_tenant', !table_expanded['tel_tenant'])" style="color: #27ae60; text-decoration: none;">
              {{ table_expanded['tel_tenant'] ? '▲ Show less' : '▼ Show ' + (telematics_tenant_table_items.length - 10) + ' more' }}
            </a>
          </div>
        </div>

        <div v-if="telematics_view_paths.length > 0" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: #2ecc71; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="telematics_show_path_table = !telematics_show_path_table">
            <span>Usage By Endpoint ({{ filtered_telematics_view_paths.length }})</span>
            <b-button
                v-if="filtered_telematics_view_paths.length > 0"
                size="sm"
                variant="light"
                v-on:click.stop="downloadUsageCsv(filtered_telematics_view_paths, path_fields, 'telematics_usage_by_endpoint')"
            >Download CSV</b-button>
          </div>
          <div v-if="telematics_show_path_table" class="mt-2" style="max-width: 480px;">
            <b-form-input
                v-model="telematics_endpoint_filter"
                placeholder="Filter by path or HTTP method..."
                size="sm"
                @input="$set(table_expanded, 'tel_path', false)"
            />
          </div>
          <b-table
              v-if="telematics_show_path_table"
              :items="_limit(filtered_telematics_view_paths, 'tel_path')"
              :fields="path_fields"
              striped hover small bordered
              sort-by="count"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(method)="data">
              {{ data.value || '—' }}
            </template>
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="telematics_show_path_table && filtered_telematics_view_paths.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'tel_path', !table_expanded['tel_path'])" style="color: #27ae60; text-decoration: none;">
              {{ table_expanded['tel_path'] ? '▲ Show less' : '▼ Show ' + (filtered_telematics_view_paths.length - 10) + ' more' }}
            </a>
          </div>
        </div>

        <div v-if="telematics_view_dates.length > 0" style="margin-top: 30px;">
          <div style="font-size: 20px; font-weight: bold; color: white; background-color: #2ecc71; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
               v-on:click="telematics_show_date_table = !telematics_show_date_table">
            <span>Usage By Date ({{ telematics_view_dates.length }})</span>
            <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(telematics_view_dates, date_fields, 'telematics_usage_by_date')">Download CSV</b-button>
          </div>
          <b-table
              v-if="telematics_show_date_table"
              :items="_limit(telematics_view_dates, 'tel_date')"
              :fields="date_fields"
              striped hover small bordered
              sort-by="date"
              :sort-desc="true"
              style="margin-top: 10px;"
          >
            <template #cell(count)="data">
              {{ data.value.toLocaleString() }}
            </template>
          </b-table>
          <div v-if="telematics_show_date_table && telematics_view_dates.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
            <a href="#" v-on:click.prevent="$set(table_expanded, 'tel_date', !table_expanded['tel_date'])" style="color: #27ae60; text-decoration: none;">
              {{ table_expanded['tel_date'] ? '▲ Show less' : '▼ Show ' + (telematics_view_dates.length - 10) + ' more' }}
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================== GOOGLE MAPS API USAGE ===================== -->
    <div v-if="!loading && gmaps_usage_data" style="margin-top: 50px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #0277bd; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between; border: 1px solid #01579b;"
           v-on:click="show_gmaps_section = !show_gmaps_section">
        <span>Google Maps API Usage</span>
        <span style="font-size: 14px; color: white;">{{ show_gmaps_section ? '▲' : '▼' }}</span>
      </div>

      <div v-if="show_gmaps_section" style="padding: 15px; background-color: #ffffff; color: #212529; border: 1px solid #dee2e6; border-top: none;">
      <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
        <span style="font-size: 16px; font-weight: bold; margin-right: 10px; color: #212529;">View:</span>
        <b-form-select v-model="gmaps_view_mode" :options="gmaps_view_mode_options" style="width: 250px;" @change="on_gmaps_view_change"></b-form-select>
      </div>

      <div style="margin-bottom: 10px;">
        <span style="font-size: 20px; font-weight: bold; color: #212529;">{{ gmaps_view_label }}: {{ gmaps_view_total.toLocaleString() }}</span>
      </div>

      <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
        <span style="font-size: 16px; font-weight: bold; margin-right: 10px; color: #212529;">Filter Chart:</span>
        <b-form-select v-model="gmaps_selected_chart_path" :options="gmaps_computed_chart_path_options" style="width: 400px;" @change="update_gmaps_chart"></b-form-select>
      </div>

      <dashboard-chart
          v-if="gmaps_requests_by_date_chart"
          :loading="loading"
          :chart_data="gmaps_requests_by_date_chart"
          chart_type="LineChart"
          :graph_title="gmaps_chart_title"
          date_format="MM/dd"
          graph_height="300"
      />

      <div v-if="!selected_tenant && gmaps_tenant_table_items.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: #1565c0; background-color: #e3f2fd; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; border: 1px solid #90caf9;"
             v-on:click="gmaps_show_tenant_table = !gmaps_show_tenant_table">
          <span>Usage By Tenant ({{ gmaps_tenant_table_items.length }})</span>
          <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(gmaps_tenant_table_items, tenant_fields, 'gmaps_usage_by_tenant')">Download CSV</b-button>
        </div>
        <b-table
            v-if="gmaps_show_tenant_table"
            :items="_limit(gmaps_tenant_table_items, 'gm_tenant')"
            :fields="tenant_fields"
            striped hover small bordered
            sort-by="count"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="gmaps_show_tenant_table && gmaps_tenant_table_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'gm_tenant', !table_expanded['gm_tenant'])" style="color: #0277bd; text-decoration: none;">
            {{ table_expanded['gm_tenant'] ? '▲ Show less' : '▼ Show ' + (gmaps_tenant_table_items.length - 10) + ' more' }}
          </a>
        </div>
      </div>

      <div v-if="gmaps_view_paths.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: #1565c0; background-color: #e3f2fd; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; border: 1px solid #90caf9;"
             v-on:click="gmaps_show_path_table = !gmaps_show_path_table">
          <span>Usage By API Type ({{ filtered_gmaps_view_paths.length }})</span>
          <b-button
              v-if="filtered_gmaps_view_paths.length > 0"
              size="sm"
              variant="light"
              v-on:click.stop="downloadUsageCsv(filtered_gmaps_view_paths, path_fields, 'gmaps_usage_by_api_type')"
          >Download CSV</b-button>
        </div>
        <div v-if="gmaps_show_path_table" class="mt-2" style="max-width: 480px;">
          <b-form-input
              v-model="gmaps_endpoint_filter"
              placeholder="Filter by path or HTTP method..."
              size="sm"
              @input="$set(table_expanded, 'gm_path', false)"
          />
        </div>
        <b-table
            v-if="gmaps_show_path_table"
            :items="_limit(filtered_gmaps_view_paths, 'gm_path')"
            :fields="path_fields"
            striped hover small bordered
            sort-by="count"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(method)="data">
            {{ data.value || '—' }}
          </template>
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="gmaps_show_path_table && filtered_gmaps_view_paths.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'gm_path', !table_expanded['gm_path'])" style="color: #0277bd; text-decoration: none;">
            {{ table_expanded['gm_path'] ? '▲ Show less' : '▼ Show ' + (filtered_gmaps_view_paths.length - 10) + ' more' }}
          </a>
        </div>
      </div>

      <div v-if="gmaps_view_dates.length > 0" style="margin-top: 30px;">
        <div style="font-size: 20px; font-weight: bold; color: #1565c0; background-color: #e3f2fd; padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px; border: 1px solid #90caf9;"
             v-on:click="gmaps_show_date_table = !gmaps_show_date_table">
          <span>Usage By Date ({{ gmaps_view_dates.length }})</span>
          <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(gmaps_view_dates, date_fields, 'gmaps_usage_by_date')">Download CSV</b-button>
        </div>
        <b-table
            v-if="gmaps_show_date_table"
            :items="_limit(gmaps_view_dates, 'gm_date')"
            :fields="date_fields"
            striped hover small bordered
            sort-by="date"
            :sort-desc="true"
            style="margin-top: 10px;"
        >
          <template #cell(count)="data">
            {{ data.value.toLocaleString() }}
          </template>
        </b-table>
        <div v-if="gmaps_show_date_table && gmaps_view_dates.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
          <a href="#" v-on:click.prevent="$set(table_expanded, 'gm_date', !table_expanded['gm_date'])" style="color: #0277bd; text-decoration: none;">
            {{ table_expanded['gm_date'] ? '▲ Show less' : '▼ Show ' + (gmaps_view_dates.length - 10) + ' more' }}
          </a>
        </div>
      </div>
      </div>
    </div>

    <div v-if="!loading && !usage_data && !tbn_usage_data && !telematics_usage_data && !gmaps_usage_data && has_loaded" class="text-center" style="margin-top: 40px;">
      <h4>No usage data found for the selected date range.</h4>
    </div>
  </div>
</template>

<script>
import { get_api_usage, get_telematics_api_usage, get_google_maps_api_usage } from '../networking/apis'
import { get_tbn_api_usage } from '../networking/tbn_apis'
import { mapState } from 'vuex'
import { justDate } from '../helpers/dates'
import moment from 'moment'
import DashboardChart from './DashboardChart'

export default {
  name: 'api_usage_dashboard',
  components: {
    DashboardChart,
  },
  data() {
    return {
      loading: false,
      has_loaded: false,
      usage_data: null,
      requests_by_date_chart: null,
      selected_chart_path: '',
      tbn_usage_data: null,
      tbn_requests_by_date_chart: null,
      tbn_selected_chart_path: '',
      selected_tenant: '',
      start_date: justDate(moment().subtract(7, 'days')),
      end_date: justDate(moment().add(1, 'days')),
      // Global filters
      global_view_mode: 'api_calls',
      global_view_mode_options: [
        { value: 'all', text: 'All Requests' },
        { value: 'api_calls', text: 'API Calls Only' },
        { value: 'cache_hits', text: 'Cache Hits Only' },
      ],
      selected_tenant_type: '',
      tenant_type_options: [
        { value: '', text: 'All Types' },
        { value: 'TBN', text: 'TBN' },
        { value: 'CM', text: 'CM' },
      ],
      // Section visibility
      show_summary_section: true,
      show_top_tenants_section: true,
      show_top_apis_section: true,
      show_internal_section: true,
      show_tenant_table: true,
      show_path_table: true,
      show_date_table: false,
      tbn_view_mode: 'api_calls',
      tbn_view_mode_options: [
        { value: 'all', text: 'All Requests' },
        { value: 'api_calls', text: 'API Calls Only' },
        { value: 'cache_hits', text: 'Cache Hits Only' },
      ],
      show_tbn_section: true,
      tbn_show_tenant_table: true,
      telematics_usage_data: null,
      telematics_requests_by_date_chart: null,
      telematics_selected_chart_path: '',
      show_telematics_section: true,
      telematics_view_mode: 'api_calls',
      telematics_view_mode_options: [
        { value: 'all', text: 'All Requests' },
        { value: 'api_calls', text: 'API Calls Only' },
        { value: 'cache_hits', text: 'Cache Hits Only' },
      ],
      telematics_show_provider_table: true,
      telematics_show_tenant_table: true,
      telematics_show_path_table: true,
      telematics_show_date_table: false,
      telematics_current_page: 1,
      telematics_provider_fields: [
        { key: 'provider', label: 'Provider', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ],
      gmaps_usage_data: null,
      gmaps_requests_by_date_chart: null,
      gmaps_selected_chart_path: '',
      show_gmaps_section: true,
      gmaps_view_mode: 'api_calls',
      gmaps_view_mode_options: [
        { value: 'all', text: 'All Requests' },
        { value: 'api_calls', text: 'API Calls Only' },
        { value: 'cache_hits', text: 'Cache Hits Only' },
      ],
      gmaps_show_tenant_table: true,
      gmaps_show_path_table: true,
      gmaps_show_date_table: false,
      gmaps_endpoint_filter: '',
      gmaps_current_page: 1,
      tbn_show_path_table: true,
      tbn_show_date_table: false,
      per_page: 25,
      current_page: 1,
      internal_endpoint_filter: '',
      tbn_endpoint_filter: '',
      telematics_endpoint_filter: '',
      tbn_current_page: 1,
      date_options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(90, 'days'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
      tenant_fields: [
        { key: 'tenant_id', label: 'Tenant', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ],
      path_fields: [
        { key: 'method', label: 'Method', sortable: true, thStyle: { width: '96px' } },
        { key: 'path', label: 'API Endpoint', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ],
      date_fields: [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ],
      table_expanded: {},
      top_apis_fields: [
        { key: 'method', label: 'Method', sortable: true, thStyle: { width: '80px' } },
        { key: 'path', label: 'Endpoint', sortable: true },
        { key: 'total', label: 'Requests', sortable: true },
      ],
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    // --- Tenant type filter helpers ---
    tenant_type_filter_ids() {
      if (!this.selected_tenant_type || !this.user || !this.user.types) return null
      const types = this.user.types
      const ids = new Set()
      if (this.user.tenant_options) {
        this.user.tenant_options.forEach(opt => {
          const tid = typeof opt === 'object' ? opt.value : opt
          if (types[tid] === this.selected_tenant_type) ids.add(tid)
        })
      }
      return ids.size > 0 ? ids : null
    },
    computed_tenant_options() {
      const options = [{ value: '', text: 'All Tenants' }]
      if (this.user && this.user.tenant_options && this.user.tenant_options.length > 0) {
        this.user.tenant_options.forEach(opt => {
          const tid = typeof opt === 'object' ? opt.value : opt
          if (this.selected_tenant_type && this.user.types && this.user.types[tid] !== this.selected_tenant_type) return
          options.push({ value: opt.value, text: opt.text })
        })
      }
      return options
    },
    // --- Summary chart ---
    summary_chart_data() {
      const si = this.show_internal_series
      const st = this.show_tbn_series
      const series = [
        ...(si ? [{ key: 'internal', label: 'Internal', arr: (this.internal_active_data && this.internal_active_data.by_date) || [] }] : []),
        ...(st ? [{ key: 'tbn',      label: 'TBN',      arr: (this.tbn_active_data && this.tbn_active_data.by_date) || [] }] : []),
        { key: 'telematics', label: 'Telematics', arr: (this.telematics_active_data && this.telematics_active_data.by_date) || [] },
        { key: 'gmaps',      label: 'Google Maps', arr: (this.gmaps_active_data && this.gmaps_active_data.by_date) || [] },
      ]

      const dateSet = new Set()
      series.forEach(s => s.arr.forEach(r => dateSet.add(String(r.date))))
      const sortedDates = Array.from(dateSet).sort()

      const maps = {}
      series.forEach(s => {
        maps[s.key] = {}
        s.arr.forEach(r => { maps[s.key][String(r.date)] = Number(r.count || 0) })
      })

      const rows = [['Date', ...series.map(s => s.label)]]
      sortedDates.forEach(d => {
        // Use 0 (not null) so every column is always a number — no type errors
        rows.push([moment(d).format('MM/DD'), ...series.map(s => maps[s.key][d] != null ? maps[s.key][d] : 0)])
      })
      return rows.length > 1 ? rows : null
    },
    summary_chart_colors() { return this.active_series_colors },
    // --- Top 10 Tenants ---
    tenant_id_to_name() {
      const map = {}
      if (this.user && this.user.tenant_options) {
        this.user.tenant_options.forEach(opt => {
          if (opt && opt.value) map[opt.value] = opt.text || opt.value
        })
      }
      return map
    },
    top_tenants_data() {
      const sources = [
        { key: 'internal', data: (this.internal_active_data && this.internal_active_data.by_tenant) || [] },
        { key: 'tbn', data: (() => {
          if (!this.tbn_usage_data) return []
          if (this.tbn_view_mode === 'api_calls' && this.tbn_usage_data.api_calls) return this.tbn_usage_data.api_calls.by_tenant || []
          if (this.tbn_view_mode === 'cache_hits' && this.tbn_usage_data.cache_hits) return this.tbn_usage_data.cache_hits.by_tenant || []
          return this.tbn_usage_data.by_tenant || []
        })() },
        { key: 'telematics', data: (() => {
          if (!this.telematics_usage_data) return []
          const d = this.telematics_usage_data
          if (this.telematics_view_mode === 'api_calls') {
            const ac = d.api_calls
            if (!ac || typeof ac !== 'object' || Array.isArray(ac)) return []
            const bt = ac.by_tenant
            if (bt && bt.length) return bt
            const fromPaths = this._aggregateTelematicsTenantsFromPathTenant(ac.by_path_tenant)
            return fromPaths.length ? fromPaths : []
          }
          if (this.telematics_view_mode === 'cache_hits') {
            const ch = d.cache_hits
            if (!ch || typeof ch !== 'object' || Array.isArray(ch)) return []
            const bt = ch.by_tenant
            if (bt && bt.length) return bt
            const fromPaths = this._aggregateTelematicsTenantsFromPathTenant(ch.by_path_tenant)
            return fromPaths.length ? fromPaths : []
          }
          return d.by_tenant || []
        })() },
        { key: 'gmaps', data: (() => {
          if (!this.gmaps_usage_data) return []
          if (this.gmaps_view_mode === 'api_calls' && this.gmaps_usage_data.api_calls) return this.gmaps_usage_data.api_calls.by_tenant || []
          if (this.gmaps_view_mode === 'cache_hits' && this.gmaps_usage_data.cache_hits) return this.gmaps_usage_data.cache_hits.by_tenant || []
          return this.gmaps_usage_data.by_tenant || []
        })() },
      ]
      const totals = {}
      for (const { key, data } of sources) {
        for (const row of data) {
          const tid = row.tenant_id
          if (!totals[tid]) totals[tid] = { tenant_id: tid, name: this.tenant_id_to_name[tid] || tid, internal: 0, tbn: 0, telematics: 0, gmaps: 0, total: 0 }
          totals[tid][key] += Number(row.count || 0)
          totals[tid].total += Number(row.count || 0)
        }
      }
      let result = Object.values(totals)
      if (this.tenant_type_filter_ids) {
        result = result.filter(r => this.tenant_type_filter_ids.has(r.tenant_id))
      }
      return result.sort((a, b) => b.total - a.total).slice(0, 10)
    },
    top_tenants_fields() {
      return [
        { key: 'name', label: 'Tenant', sortable: true },
        ...(this.show_internal_series ? [{ key: 'internal', label: 'Internal', sortable: true }] : []),
        ...(this.show_tbn_series ? [{ key: 'tbn', label: 'TBN', sortable: true }] : []),
        { key: 'telematics', label: 'Telematics', sortable: true },
        { key: 'gmaps', label: 'Google Maps', sortable: true },
        { key: 'total', label: 'Total', sortable: true },
      ]
    },
    top_tenants_chart_data() {
      if (!this.top_tenants_data.length) return null
      const si = this.show_internal_series
      const st = this.show_tbn_series
      const header = ['Tenant', ...(si ? ['Internal'] : []), ...(st ? ['TBN'] : []), 'Telematics', 'Google Maps']
      const rows = [header]
      this.top_tenants_data.forEach(r => {
        rows.push([r.name, ...(si ? [r.internal || 0] : []), ...(st ? [r.tbn || 0] : []), r.telematics || 0, r.gmaps || 0])
      })
      return rows
    },
    top_tenants_chart_colors() { return this.active_series_colors },
    // --- Top 10 API Endpoints ---
    top_apis_data() {
      const sources = [
        { key: 'internal', data: (this.internal_active_data && this.internal_active_data.by_path) || [] },
        { key: 'tbn', data: (this.tbn_active_data && this.tbn_active_data.by_path) || [] },
        { key: 'telematics', data: (this.telematics_active_data && this.telematics_active_data.by_path) || [] },
        { key: 'gmaps', data: (this.gmaps_active_data && this.gmaps_active_data.by_path) || [] },
      ]
      const totals = {}
      for (const { key, data } of sources) {
        for (const row of data) {
          const label = (row.method ? row.method + ' ' : '') + (row.path || '')
          if (!totals[label]) totals[label] = { method: row.method || '', path: row.path || '', label, internal: 0, tbn: 0, telematics: 0, gmaps: 0, total: 0 }
          totals[label][key] += Number(row.count || 0)
          totals[label].total += Number(row.count || 0)
        }
      }
      return Object.values(totals).sort((a, b) => b.total - a.total).slice(0, 10)
    },
    top_apis_chart_data() {
      if (!this.top_apis_data.length) return null
      const si = this.show_internal_series
      const st = this.show_tbn_series
      const header = ['Endpoint', ...(si ? ['Internal'] : []), ...(st ? ['TBN'] : []), 'Telematics', 'Google Maps']
      const rows = [header]
      this.top_apis_data.forEach(r => {
        rows.push([r.label, ...(si ? [r.internal || 0] : []), ...(st ? [r.tbn || 0] : []), r.telematics || 0, r.gmaps || 0])
      })
      return rows
    },
    top_apis_chart_colors() { return this.active_series_colors },
    top_tenants_chart_area() {
      const maxLen = this.top_tenants_data.reduce((m, r) => Math.max(m, (r.name || '').length), 0)
      const pct = Math.min(40, Math.max(16, Math.ceil(maxLen * 0.72)))
      return { left: `${pct}%`, right: '8%', top: 40, bottom: 40 }
    },
    top_apis_chart_area() {
      const maxLen = this.top_apis_data.reduce((m, r) => Math.max(m, (r.label || '').length), 0)
      const pct = Math.min(48, Math.max(20, Math.ceil(maxLen * 0.72)))
      return { left: `${pct}%`, right: '8%', top: 40, bottom: 40 }
    },
    // --- Existing computed ---
    filtered_internal_by_path() {
      const rows = (this.usage_data && this.usage_data.by_path) ? this.usage_data.by_path : []
      const q = (this.internal_endpoint_filter || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter(item => {
        const path = String(item.path || '').toLowerCase()
        const method = String(item.method || '').toLowerCase()
        return path.includes(q) || method.includes(q)
      })
    },
    internal_tenant_table_items() {
      if (!this.usage_data || !this.usage_data.by_tenant || !this.usage_data.by_tenant.length) return []
      if (!this.selected_chart_path) return this._apply_tenant_type_filter(this.usage_data.by_tenant)
      const m = this.usage_data.by_path_tenant
      const ck = this.selected_chart_path
      if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
      if (m) return []
      return this._apply_tenant_type_filter(this.usage_data.by_tenant)
    },
    computed_chart_path_options() {
      const options = [{ value: '', text: 'All Endpoints' }]
      if (this.usage_data && this.usage_data.by_path) {
        this.usage_data.by_path.forEach(item => {
          const value = item.chart_key != null && item.chart_key !== '' ? item.chart_key : item.path
          const meth = item.method ? `${item.method} ` : ''
          options.push({
            value,
            text: `${meth}${item.path} (${item.count.toLocaleString()})`,
          })
        })
      }
      return options
    },
    chart_title() {
      if (this.selected_chart_path) {
        return 'Requests By Date — ' + this.selected_chart_path
      }
      return 'Requests By Date'
    },
    internal_active_data() {
      if (!this.usage_data) return null
      if (this.global_view_mode === 'cache_hits') return { by_date: [], by_path: [], by_tenant: [] }
      return this.usage_data
    },
    show_internal_series() {
      return this.global_view_mode !== 'cache_hits'
    },
    show_tbn_series() {
      return this.selected_tenant_type !== 'CM'
    },
    active_series_colors() {
      const colors = []
      if (this.show_internal_series) colors.push('black')
      if (this.show_tbn_series) colors.push('orange')
      colors.push('green')
      colors.push('blue')
      return colors.length < 4 ? colors : null
    },
    tbn_active_data() {
      if (!this.tbn_usage_data) return null
      if (this.tbn_view_mode === 'api_calls' && this.tbn_usage_data.api_calls) return this.tbn_usage_data.api_calls
      if (this.tbn_view_mode === 'cache_hits' && this.tbn_usage_data.cache_hits) return this.tbn_usage_data.cache_hits
      return { by_path: this.tbn_usage_data.by_path, by_date: this.tbn_usage_data.by_date, by_path_date: this.tbn_usage_data.by_path_date }
    },
    tbn_show_response_size() {
      return this.tbn_view_mode !== 'cache_hits'
    },
    tbn_tenant_fields() {
      const fields = [
        { key: 'tenant_id', label: 'Tenant', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ]
      if (this.tbn_show_response_size) fields.push({ key: 'avg_response_bytes', label: 'Avg Response Size', sortable: true, formatter: v => v ? this.formatBytes(v) : '—' })
      return fields
    },
    tbn_path_fields() {
      const fields = [
        { key: 'method', label: 'Method', sortable: true, thStyle: { width: '96px' } },
        { key: 'path', label: 'API Endpoint', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ]
      if (this.tbn_show_response_size) fields.push({ key: 'avg_response_bytes', label: 'Avg Response Size', sortable: true, formatter: v => v ? this.formatBytes(v) : '—' })
      return fields
    },
    tbn_date_fields() {
      const fields = [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'count', label: 'Requests', sortable: true },
      ]
      if (this.tbn_show_response_size) fields.push({ key: 'avg_response_bytes', label: 'Avg Response Size', sortable: true, formatter: v => v ? this.formatBytes(v) : '—' })
      return fields
    },
    tbn_view_total() {
      if (!this.tbn_usage_data) return 0
      if (this.tbn_view_mode === 'api_calls') return this.tbn_usage_data.total_api_calls || 0
      if (this.tbn_view_mode === 'cache_hits') return this.tbn_usage_data.total_cache_hits || 0
      return this.tbn_usage_data.total_requests || 0
    },
    tbn_view_label() {
      if (this.tbn_view_mode === 'api_calls') return 'Total API Calls'
      if (this.tbn_view_mode === 'cache_hits') return 'Total Cache Hits'
      return 'Total Requests'
    },
    tbn_view_paths() {
      if (!this.tbn_active_data) return []
      return this.tbn_active_data.by_path || []
    },
    filtered_tbn_view_paths() {
      const rows = this.tbn_view_paths
      const q = (this.tbn_endpoint_filter || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter(item => {
        const path = String(item.path || '').toLowerCase()
        const method = String(item.method || '').toLowerCase()
        return path.includes(q) || method.includes(q)
      })
    },
    tbn_tenant_table_items() {
      if (!this.tbn_usage_data || this.selected_tenant) return []
      const d = this.tbn_usage_data

      const baseByMode = () => {
        if (this.tbn_view_mode === 'api_calls') {
          const nested = d.api_calls && d.api_calls.by_tenant
          if (nested && nested.length) return nested
          return d.by_tenant || []
        }
        if (this.tbn_view_mode === 'cache_hits') {
          const nested = d.cache_hits && d.cache_hits.by_tenant
          if (nested && nested.length) return nested
          return d.by_tenant || []
        }
        return d.by_tenant || []
      }

      if (!this.tbn_selected_chart_path) return this._apply_tenant_type_filter(baseByMode())

      const ck = this.tbn_selected_chart_path
      if (this.tbn_view_mode === 'api_calls') {
        const m = d.api_calls && d.api_calls.by_path_tenant
        if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
        if (m) return []
        return this._apply_tenant_type_filter(baseByMode())
      }
      if (this.tbn_view_mode === 'cache_hits') {
        const m = d.cache_hits && d.cache_hits.by_path_tenant
        if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
        if (m) return []
        return this._apply_tenant_type_filter(baseByMode())
      }
      const m = d.by_path_tenant
      if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
      if (m) return []
      return this._apply_tenant_type_filter(baseByMode())
    },
    tbn_view_dates() {
      if (!this.tbn_active_data) return []
      return this.tbn_active_data.by_date || []
    },
    tbn_computed_chart_path_options() {
      const options = [{ value: '', text: 'All Endpoints' }]
      if (this.tbn_view_paths) {
        this.tbn_view_paths.forEach(item => {
          const value = item.chart_key != null && item.chart_key !== '' ? item.chart_key : item.path
          const meth = item.method ? `${item.method} ` : ''
          options.push({
            value,
            text: `${meth}${item.path} (${item.count.toLocaleString()})`,
          })
        })
      }
      return options
    },
    telematics_active_data() {
      if (!this.telematics_usage_data) return null
      const d = this.telematics_usage_data
      const isObj = x => x && typeof x === 'object' && !Array.isArray(x)
      if (this.telematics_view_mode === 'api_calls' && isObj(d.api_calls)) return d.api_calls
      if (this.telematics_view_mode === 'cache_hits' && isObj(d.cache_hits)) return d.cache_hits
      return { by_path: d.by_path, by_date: d.by_date, by_path_date: d.by_path_date }
    },
    telematics_view_total() {
      if (!this.telematics_usage_data) return 0
      const n = v => {
        const x = Number(v)
        return Number.isFinite(x) ? x : 0
      }
      const d = this.telematics_usage_data
      if (this.telematics_view_mode === 'api_calls') return n(d.total_api_calls)
      if (this.telematics_view_mode === 'cache_hits') return n(d.total_cache_hits)
      return n(d.total_requests)
    },
    telematics_view_label() {
      if (this.telematics_view_mode === 'api_calls') return 'Total API Calls'
      if (this.telematics_view_mode === 'cache_hits') return 'Total Cache Hits'
      return 'Total Requests'
    },
    telematics_view_paths() {
      if (!this.telematics_active_data) return []
      return this.telematics_active_data.by_path || []
    },
    filtered_telematics_view_paths() {
      const rows = this.telematics_view_paths
      const q = (this.telematics_endpoint_filter || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter(item => {
        const path = String(item.path || '').toLowerCase()
        const method = String(item.method || '').toLowerCase()
        return path.includes(q) || method.includes(q)
      })
    },
    telematics_view_dates() {
      if (!this.telematics_active_data) return []
      return this.telematics_active_data.by_date || []
    },
    telematics_tenant_table_items() {
      if (!this.telematics_usage_data || !this.telematics_usage_data.by_tenant) return []
      const d = this.telematics_usage_data
      if (!this.telematics_selected_chart_path) return this._apply_tenant_type_filter(d.by_tenant)
      const m = d.by_path_tenant
      const ck = this.telematics_selected_chart_path
      if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
      if (m) return []
      return this._apply_tenant_type_filter(d.by_tenant)
    },
    telematics_computed_chart_path_options() {
      const options = [{ value: '', text: 'All Endpoints' }]
      if (this.telematics_view_paths) {
        this.telematics_view_paths.forEach(item => {
          const value = item.chart_key != null && item.chart_key !== '' ? item.chart_key : item.path
          const meth = item.method ? `${item.method} ` : ''
          options.push({
            value,
            text: `${meth}${item.path} (${item.count.toLocaleString()})`,
          })
        })
      }
      return options
    },
    telematics_chart_title() {
      const suffix = this.telematics_view_mode === 'api_calls' ? ' (API Calls)' : this.telematics_view_mode === 'cache_hits' ? ' (Cache Hits)' : ''
      if (this.telematics_selected_chart_path) {
        return 'Telematics Requests By Date — ' + this.telematics_selected_chart_path + suffix
      }
      return 'Telematics Requests By Date' + suffix
    },
    tbn_chart_title() {
      const suffix = this.tbn_view_mode === 'api_calls' ? ' (API Calls)' : this.tbn_view_mode === 'cache_hits' ? ' (Cache Hits)' : ''
      if (this.tbn_selected_chart_path) {
        return 'TBN Requests By Date — ' + this.tbn_selected_chart_path + suffix
      }
      return 'TBN Requests By Date' + suffix
    },
    gmaps_active_data() {
      if (!this.gmaps_usage_data) return null
      if (this.gmaps_view_mode === 'api_calls' && this.gmaps_usage_data.api_calls) return this.gmaps_usage_data.api_calls
      if (this.gmaps_view_mode === 'cache_hits' && this.gmaps_usage_data.cache_hits) return this.gmaps_usage_data.cache_hits
      return { by_path: this.gmaps_usage_data.by_path, by_date: this.gmaps_usage_data.by_date, by_path_date: this.gmaps_usage_data.by_path_date }
    },
    gmaps_view_total() {
      if (!this.gmaps_usage_data) return 0
      if (this.gmaps_view_mode === 'api_calls') return this.gmaps_usage_data.total_api_calls || 0
      if (this.gmaps_view_mode === 'cache_hits') return this.gmaps_usage_data.total_cache_hits || 0
      return this.gmaps_usage_data.total_requests || 0
    },
    gmaps_view_label() {
      if (this.gmaps_view_mode === 'api_calls') return 'Total API Calls'
      if (this.gmaps_view_mode === 'cache_hits') return 'Total Cache Hits'
      return 'Total Requests'
    },
    gmaps_view_paths() {
      if (!this.gmaps_active_data) return []
      return this.gmaps_active_data.by_path || []
    },
    gmaps_view_dates() {
      if (!this.gmaps_active_data) return []
      return this.gmaps_active_data.by_date || []
    },
    gmaps_tenant_table_items() {
      if (!this.gmaps_usage_data || this.selected_tenant) return []
      const d = this.gmaps_usage_data
      if (!this.gmaps_selected_chart_path) {
        if (this.gmaps_view_mode === 'api_calls') {
          const nested = d.api_calls && d.api_calls.by_tenant
          if (nested && nested.length) return this._apply_tenant_type_filter(nested)
        }
        if (this.gmaps_view_mode === 'cache_hits') {
          const nested = d.cache_hits && d.cache_hits.by_tenant
          if (nested && nested.length) return this._apply_tenant_type_filter(nested)
        }
        return this._apply_tenant_type_filter(d.by_tenant || [])
      }
      const ck = this.gmaps_selected_chart_path
      if (this.gmaps_view_mode === 'api_calls') {
        const m = d.api_calls && d.api_calls.by_path_tenant
        if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
        if (m) return []
      }
      if (this.gmaps_view_mode === 'cache_hits') {
        const m = d.cache_hits && d.cache_hits.by_path_tenant
        if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
        if (m) return []
      }
      const m = d.by_path_tenant
      if (m && Object.prototype.hasOwnProperty.call(m, ck)) return this._apply_tenant_type_filter(m[ck])
      if (m) return []
      return this._apply_tenant_type_filter(d.by_tenant || [])
    },
    filtered_gmaps_view_paths() {
      const rows = this.gmaps_view_paths
      const q = (this.gmaps_endpoint_filter || '').trim().toLowerCase()
      if (!q) return rows
      return rows.filter(item => {
        const path = String(item.path || '').toLowerCase()
        const method = String(item.method || '').toLowerCase()
        return path.includes(q) || method.includes(q)
      })
    },
    gmaps_computed_chart_path_options() {
      const options = [{ value: '', text: 'All API Types' }]
      if (this.gmaps_view_paths) {
        this.gmaps_view_paths.forEach(item => {
          const value = item.chart_key != null && item.chart_key !== '' ? item.chart_key : item.path
          const meth = item.method ? `${item.method} ` : ''
          options.push({
            value,
            text: `${meth}${item.path} (${item.count.toLocaleString()})`,
          })
        })
      }
      return options
    },
    gmaps_chart_title() {
      const suffix = this.gmaps_view_mode === 'api_calls' ? ' (API Calls)' : this.gmaps_view_mode === 'cache_hits' ? ' (Cache Hits)' : ''
      if (this.gmaps_selected_chart_path) {
        return 'Google Maps Requests By Date — ' + this.gmaps_selected_chart_path + suffix
      }
      return 'Google Maps Requests By Date' + suffix
    },
  },
  watch: {
    global_view_mode(newVal) {
      this.tbn_view_mode = newVal
      this.telematics_view_mode = newVal
      this.gmaps_view_mode = newVal
      this.on_tbn_view_change()
      this.on_telematics_view_change()
      this.on_gmaps_view_change()
    },
  },
  async created() {
    await this.load_data()
  },
  methods: {
    _limit(items, key) {
      if (!items) return []
      return this.table_expanded[key] ? items : items.slice(0, 10)
    },
    _apply_tenant_type_filter(items) {
      if (!this.tenant_type_filter_ids || !items || !items.length) return items || []
      return items.filter(r => this.tenant_type_filter_ids.has(r.tenant_id))
    },
    /** Sum per-tenant counts from telematics api_calls/cache_hits by_path_tenant (chart_key -> rows). */
    _aggregateTelematicsTenantsFromPathTenant(pathTenantMap) {
      if (!pathTenantMap || typeof pathTenantMap !== 'object' || Array.isArray(pathTenantMap)) return []
      const totals = {}
      for (const chartKey of Object.keys(pathTenantMap)) {
        const rows = pathTenantMap[chartKey]
        if (!Array.isArray(rows)) continue
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i]
          const tid = row.tenant_id
          if (!tid) continue
          totals[tid] = (totals[tid] || 0) + Number(row.count || 0)
        }
      }
      return Object.keys(totals)
        .map(tenant_id => ({ tenant_id, count: totals[tenant_id] }))
        .sort((a, b) => b.count - a.count)
    },
    formatBytes(bytes) {
      if (!bytes || bytes === 0) return '0 B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    _fieldKeys(fields) {
      return fields.map(f => (typeof f === 'string' ? f : f.key))
    },
    _fieldLabels(fields) {
      return fields.map(f => (typeof f === 'string' ? f : (f.label || f.key)))
    },
    _csvEscapeField(val) {
      if (val == null || val === '') return ''
      const s = String(val)
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    },
    _usageCsvDateSuffix() {
      const sd = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ed = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      return `${sd}_to_${ed}`
    },
    downloadUsageCsv(items, fields, baseFilename) {
      if (!items || !items.length || !fields || !fields.length) return
      const keys = this._fieldKeys(fields)
      const headers = this._fieldLabels(fields)
      const lines = [headers.map(h => this._csvEscapeField(h)).join(',')]
      for (let i = 0; i < items.length; i++) {
        const row = items[i]
        lines.push(keys.map(k => this._csvEscapeField(row[k])).join(','))
      }
      const safe = String(baseFilename || 'usage').replace(/[^\w\-]+/g, '_')
      const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${safe}_${this._usageCsvDateSuffix()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    shift_week(weeks) {
      const from = moment(this.start_date, 'MM-DD-YYYY').add(weeks, 'weeks')
      const to = moment(this.end_date, 'MM-DD-YYYY').add(weeks, 'weeks')
      this.start_date = justDate(from)
      this.end_date = justDate(to)
      this.load_data()
    },
    _build_chart_data(date_data) {
      if (date_data && date_data.length > 0) {
        return [
          ['Date', 'Requests'],
          ...date_data.map(({ date, count }) => [
            moment(String(date)).format('MM/DD'),
            Number(count || 0),
          ])
        ]
      }
      return null
    },
    update_chart() {
      if (!this.usage_data) return
      let date_data = null
      if (this.selected_chart_path && this.usage_data.by_path_date && this.usage_data.by_path_date[this.selected_chart_path]) {
        date_data = this.usage_data.by_path_date[this.selected_chart_path]
      } else {
        date_data = this.usage_data.by_date
      }
      this.requests_by_date_chart = this._build_chart_data(date_data)
    },
    update_tbn_chart() {
      if (!this.tbn_active_data) return
      let date_data = null
      const pd = this.tbn_active_data.by_path_date
      if (this.tbn_selected_chart_path && pd && pd[this.tbn_selected_chart_path]) {
        date_data = pd[this.tbn_selected_chart_path]
      } else {
        date_data = this.tbn_active_data.by_date
      }
      this.tbn_requests_by_date_chart = this._build_chart_data(date_data)
    },
    update_telematics_chart() {
      if (!this.telematics_active_data) return
      let date_data = null
      const pd = this.telematics_active_data.by_path_date
      if (this.telematics_selected_chart_path && pd && pd[this.telematics_selected_chart_path]) {
        date_data = pd[this.telematics_selected_chart_path]
      } else {
        date_data = this.telematics_active_data.by_date
      }
      this.telematics_requests_by_date_chart = this._build_chart_data(date_data)
    },
    on_telematics_view_change() {
      this.telematics_selected_chart_path = ''
      this.telematics_current_page = 1
      this.update_telematics_chart()
    },
    on_tbn_view_change() {
      this.tbn_selected_chart_path = ''
      this.tbn_current_page = 1
      this.update_tbn_chart()
    },
    update_gmaps_chart() {
      if (!this.gmaps_active_data) return
      let date_data = null
      const pd = this.gmaps_active_data.by_path_date
      if (this.gmaps_selected_chart_path && pd && pd[this.gmaps_selected_chart_path]) {
        date_data = pd[this.gmaps_selected_chart_path]
      } else {
        date_data = this.gmaps_active_data.by_date
      }
      this.gmaps_requests_by_date_chart = this._build_chart_data(date_data)
    },
    on_gmaps_view_change() {
      this.gmaps_selected_chart_path = ''
      this.gmaps_current_page = 1
      this.update_gmaps_chart()
    },
    async load_data() {
      this.loading = true
      this.usage_data = null
      this.tbn_usage_data = null
      this.telematics_usage_data = null
      this.gmaps_usage_data = null
      this.requests_by_date_chart = null
      this.tbn_requests_by_date_chart = null
      this.telematics_requests_by_date_chart = null
      this.gmaps_requests_by_date_chart = null
      this.selected_chart_path = ''
      this.tbn_selected_chart_path = ''
      this.telematics_selected_chart_path = ''
      this.gmaps_selected_chart_path = ''
      this.telematics_view_mode = 'api_calls'
      this.tbn_view_mode = 'api_calls'
      this.gmaps_view_mode = 'api_calls'
      this.global_view_mode = 'api_calls'
      this.table_expanded = {}
      this.current_page = 1
      this.tbn_current_page = 1
      this.telematics_current_page = 1
      this.internal_endpoint_filter = ''
      this.tbn_endpoint_filter = ''
      this.telematics_endpoint_filter = ''
      this.gmaps_endpoint_filter = ''
      this.gmaps_current_page = 1

      const sd = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ed = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const tenant = this.selected_tenant === '' ? '' : this.selected_tenant
      const tenant_ids = (!tenant && this.selected_tenant_type && this.user && this.user.types)
        ? Object.entries(this.user.types).filter(([, t]) => t === this.selected_tenant_type).map(([id]) => id)
        : null

      const [internal_result, tbn_result, telematics_result, gmaps_result] = await Promise.allSettled([
        get_api_usage(tenant, sd, ed, tenant_ids),
        get_tbn_api_usage(tenant, sd, ed, tenant_ids),
        get_telematics_api_usage(tenant, sd, ed, tenant_ids),
        get_google_maps_api_usage(tenant, sd, ed, tenant_ids),
      ])

      if (internal_result.status === 'fulfilled') {
        try {
          this.usage_data = internal_result.value.value.data
          this.update_chart()
        } catch (e) {
          this.usage_data = null
        }
      }

      if (tbn_result.status === 'fulfilled') {
        try {
          this.tbn_usage_data = tbn_result.value.value.data
          this.update_tbn_chart()
        } catch (e) {
          this.tbn_usage_data = null
        }
      }

      if (telematics_result.status === 'fulfilled') {
        try {
          this.telematics_usage_data = telematics_result.value.value.data
          this.update_telematics_chart()
        } catch (e) {
          this.telematics_usage_data = null
        }
      }

      if (gmaps_result.status === 'fulfilled') {
        try {
          this.gmaps_usage_data = gmaps_result.value.value.data
          this.update_gmaps_chart()
        } catch (e) {
          this.gmaps_usage_data = null
        }
      }

      this.loading = false
      this.has_loaded = true
    },
  }
}
</script>
